import { useContext, useEffect, useLayoutEffect, useMemo, type ReactNode } from "react";
import { App as AntdApp, ConfigProvider, theme as antdTheme } from "antd";
import { enUSIntl, ProProvider } from "@ant-design/pro-components";
import enUS from "antd/locale/en_US";

import { brandComponents, brandTokens } from "@/lib/brandTheme";
import { keepErrorToasts } from "@/lib/mutations";
import { useUiStore } from "@/stores/uiStore";

/** Failed actions stay on screen long enough to be read (lib/mutations.ts). Renders
 *  nothing: it only needs to sit inside the antd App to reach its toasts. */
function ErrorToastPolicy() {
  const { message, notification } = AntdApp.useApp();
  useLayoutEffect(
    () => keepErrorToasts(message, notification),
    [message, notification],
  );
  return null;
}

/** The table library's English reads "Selected 3 Item" above a table with
 *  ticked rows; ours reads "Selected: 3", right for one row and for many. */
const TABLE_WORDS: Record<string, string> = {
  "alert.selected": "Selected:",
  "alert.item": "",
};
const consoleIntl: typeof enUSIntl = {
  locale: enUSIntl.locale,
  getMessage: (id, defaultMessage) =>
    TABLE_WORDS[id] ?? enUSIntl.getMessage(id, defaultMessage),
};

/** Applies the persisted light/dark theme to Ant Design + the page background. */
function ThemedApp({ children }: { children: ReactNode }) {
  const mode = useUiStore((state) => state.theme);
  const isDark = mode === "dark";
  const proContext = useContext(ProProvider);
  const proValue = useMemo(() => ({ ...proContext, intl: consoleIntl }), [proContext]);

  useEffect(() => {
    document.body.style.background = isDark ? "#141414" : "";
    document.documentElement.dataset.theme = mode;
  }, [isDark, mode]);

  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        algorithm: isDark
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        // OmniCloud brand accent — a deliberate identity instead of stock antd
        // blue (#1677ff), which made the console read as a generic template.
        // Single source of truth for the primary colour; adjust to match final
        // brand guidelines (logo/marketing).
        // Links are lighter on the dark theme so they meet WCAG AA there
        // (lib/brandTheme.ts).
        token: brandTokens(isDark),
        components: brandComponents(isDark),
      }}
    >
      {/* Notifications open on the left: every drawer opens on the right, and
          a notification is drawn above it, so on the right it lay over the
          drawer's fields and buttons and took their clicks (M-23). */}
      <AntdApp notification={{ placement: "topLeft" }}>
        <ErrorToastPolicy />
        <ProProvider.Provider value={proValue}>{children}</ProProvider.Provider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default ThemedApp;
